/* eslint-disable */
import dayjs from "dayjs";
import {
  ConnectionTypeEnum,
  EICStatusEnum,
  OperationalTeamEnum,
  PremiseDetails,
  PremiseRequest,
  PremisesTableRow,
  PremisesTableRows,
  RequestStatusEnum,
  RequestTypeEnum,
  SiteDetails,
  SortByEnum,
} from "./types";
import {
  getPremiseTypeFromKrakenValue,
  getStreetTypeFromKrakenValue,
} from "../Form/helper";
import { PremiseDetails as ChangePremiseDetails } from "../Changes/types";
import { formatDpNumber } from "../Form/helpers";
import { apiClient } from "src/api/apiService";

export const formatDate = (dateString: string): string => {
  const date = dayjs(dateString);
  return date.format("DD.MM.YYYY");
};

export const getFormattedAddress = (
  addressObj: PremiseDetails | ChangePremiseDetails
) => {
  const {
    unit_type,
    unit_number,
    floor_number,
    site_details: siteDetails,
  } = addressObj;
  const {
    street_number,
    street_name,
    street_type,
    suburb,
    state,
    postcode,
    lot_number,
    dp_number,
    block_number,
    section_number,
    building_name,
    street_suffix,
  } = siteDetails;

  const unitDetails = unit_number
    ? `${
        unit_type ? getPremiseTypeFromKrakenValue(unit_type) : "Unit"
      } ${unit_number}, `
    : "";

  const floorDetails = floor_number ? `Floor ${floor_number}, ` : "";

  const decodedBlockNumber = block_number ? `Block ${block_number}, ` : "";
  const decodedSectionNumber = section_number
    ? `Section ${section_number}, `
    : "";
  const decodedDpNumber = formatDpNumber(dp_number, state);
  const decodedBuildingName = building_name ? `${building_name}, ` : "";
  const decodedLotNumber = lot_number ? `Lot ${lot_number}, ` : "";
  const decodedStreetNumber = street_number ?? "";
  const streetDetails = `${decodedBuildingName}${floorDetails}${unitDetails}${decodedBlockNumber}${decodedSectionNumber}${decodedDpNumber}${decodedLotNumber}${decodedStreetNumber} ${street_name} ${getStreetTypeFromKrakenValue(
    street_type
  )}${
    street_suffix ? ` ${street_suffix}` : ""
  }, ${suburb}, ${state} ${postcode}`;
  return streetDetails;
};

export const mapRequestsToRows = (
  requests: PremiseRequest[]
): PremisesTableRows => {
  return requests.map((request) => {
    const addressObj = request.premise;
    const premiseRequestSummary = request.premise_request_summary;
    const address = getFormattedAddress(addressObj);
    const isDraft =
      premiseRequestSummary.request_status === RequestStatusEnum.DRAFT ||
      premiseRequestSummary.request_status === RequestStatusEnum.DRAFT_ASSIGNED;
    return {
      serviceType: premiseRequestSummary.service_type,
      connectionType: premiseRequestSummary.connection_type,
      address,
      id: premiseRequestSummary.request_id,
      journeyId: premiseRequestSummary.journey_id,
      distributorApplicationId:
        premiseRequestSummary.distributor_application_id,
      nmi: premiseRequestSummary.service_identifier,
      lodgedDate: isDraft ? null : formatDate(premiseRequestSummary.submitted),
      etaDate: formatDate(premiseRequestSummary.expected),
      updatedDate: isDraft ? null : formatDate(premiseRequestSummary.updated),
      status: getStatusIconString(
        premiseRequestSummary.request_status,
        premiseRequestSummary.message_pause,
        premiseRequestSummary.request_type
      ),
      statusText: getStatusText(
        premiseRequestSummary.request_status,
        getSmallCase(premiseRequestSummary.raw_request_status),
        premiseRequestSummary.connection_type,
        addressObj.site_details.state,
        premiseRequestSummary.collaborator_name || "",
        premiseRequestSummary.message_pause,
        premiseRequestSummary.eic_status ?? EICStatusEnum.EIC_COMM_UNUSED
      ),
      hasUnreadMessage:
        premiseRequestSummary.has_unread_message_for_user === undefined
          ? null
          : premiseRequestSummary.has_unread_message_for_user,
      requestStatus: premiseRequestSummary.request_status,
      state: addressObj.site_details.state,
      requestChannel: premiseRequestSummary.request_channel,
      operationalTeam: getOperationalTeamText(
        premiseRequestSummary.operational_team
      ),
      eicStatus: premiseRequestSummary.eic_status,
      premiseId: addressObj.id,
    };
  });
};

export const normaliseIdColumnValue = (
  requestId: string,
  journeyId: string | null,
  distributorApplicationId: string | null
): string => {
  if (journeyId) {
    return `J ${journeyId}`;
  }
  if (distributorApplicationId) {
    return `DB ${distributorApplicationId}`;
  }
  return requestId;
};

export const mapPremisesTableRowKeyToSortByEnum = (
  key: keyof PremisesTableRow
): SortByEnum | null => {
  const keyToSortByEnumMap: Record<keyof PremisesTableRow, SortByEnum | null> =
    {
      serviceType: SortByEnum.SERVICE_TYPE,
      connectionType: SortByEnum.CONNECTION_TYPE,
      address: SortByEnum.ADDRESS,
      id: SortByEnum.REQUEST_ID,
      nmi: null,
      lodgedDate: SortByEnum.SUBMITTED,
      updatedDate: SortByEnum.UPDATED,
      status: SortByEnum.REQUEST_STATUS,
      statusText: SortByEnum.REQUEST_STATUS,
      hasUnreadMessage: SortByEnum.MESSAGE,
      journeyId: null,
      distributorApplicationId: null,
      requestStatus: null,
      state: null,
      requestChannel: null,
      operationalTeam: null,
      eicStatus: null,
      premiseId: null,
    };

  return keyToSortByEnumMap[key] || null;
};

export const getOperationalTeamText = (
  operationalTeamEnum: OperationalTeamEnum | null
): string => {
  switch (operationalTeamEnum) {
    case OperationalTeamEnum.TIER_1:
      return "Tier 1";
    case OperationalTeamEnum.TIER_2:
      return "Tier 2";
    case OperationalTeamEnum.TIER_3:
      return "Tier 3";
    case OperationalTeamEnum.CES:
      return "CES";
    case OperationalTeamEnum.MSBG:
      return "MSBG";
    case OperationalTeamEnum.MSBG_COUNCIL:
      return "MSBG Council";
    case OperationalTeamEnum.MSBG_TELCO:
      return "MSBG Telco";
    default:
      return "-";
  }
};

export const getStatusText = (
  requestStatus: RequestStatusEnum,
  rawRequestStatus: string,
  connectionType: ConnectionTypeEnum,
  state: string,
  collaboratorName: string,
  messagePause: boolean,
  eicStatus?: EICStatusEnum
): string => {
  if (messagePause) {
    return requestStatus === RequestStatusEnum.CT_SUBMITTED
      ? "Close/transfer processing paused. Read message!"
      : "Processing paused. Read message!";
  }

  const statusMessages = {
    [RequestStatusEnum.NONE]: "n/a - No status is displayed, should be blank",
    [RequestStatusEnum.DISCARDED]:
      "n/a - Request should not appear in dashboard",
    [RequestStatusEnum.IN_PROGRESS]: "We've received your request",
    [RequestStatusEnum.FAILED]: "In Progress", // TODO: check if this is correct
    [RequestStatusEnum.CANCELLED]: "Cancelled. Check messages for details",
    [RequestStatusEnum.CT_SUBMITTED]: "Close/transfer request received",
    [RequestStatusEnum.AWAITING_ACCOUNT_HOLDER]:
      "Awaiting account holder details",
    [RequestStatusEnum.PROCESSING]: "We’ve received your request",
    [RequestStatusEnum.REVIEW]: "We’re reviewing your request",
    [RequestStatusEnum.SCHEDULED]: "Pre-install scheduled",
    [RequestStatusEnum.EIC_SENT]: "Waiting on account holder",
  };

  if (
    connectionType === ConnectionTypeEnum.CAN_RETAIL &&
    requestStatus === RequestStatusEnum.CANCELLED
  ) {
    return "Cancelled";
  }
  const finishConnectedStatuses = {
    service_line_completed:
      state === "NSW"
        ? "Path valve connected, please request meter"
        : "Gas line connected, please request meter",
    booked: "Account closure request booked",
    complete: "All done",
    connected: "All done",
    completed: "All done",
  };

  const connectedStatus = "Connected";

  const completedStatus = "Connected, please book closure";

  // Early returns for simple status messages
  if (statusMessages.hasOwnProperty(requestStatus)) {
    return statusMessages[requestStatus as keyof typeof statusMessages];
  }

  if (requestStatus === RequestStatusEnum.DRAFT) {
    switch (eicStatus) {
      case EICStatusEnum.EIC_COMM_SENT:
        return "Waiting on account holder";
      case EICStatusEnum.EIC_ACCEPTED:
        return "Account holder details received";
      default:
        return "Draft";
    }
  }

  if (requestStatus === RequestStatusEnum.DRAFT_ASSIGNED) {
    switch (eicStatus) {
      case EICStatusEnum.EIC_COMM_SENT:
        return "Waiting on account holder";
      default:
        return `Assigned to ${collaboratorName}`;
    }
  }

  // Handling LODGED status
  if (requestStatus === RequestStatusEnum.LODGED) {
    const lodgedStatuses = {
      nmi_requested: "NMI request received - with distributor",
      nmi_allocated: "The NMI is allocated",
      connection_requested: [
        ConnectionTypeEnum.PERMANENT,
        ConnectionTypeEnum.TEMPORARY,
        ConnectionTypeEnum.TEMPPERM,
        ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX,
      ].includes(connectionType)
        ? state === "VIC"
          ? "Connection requested - with distributor"
          : "Connection requested - with meter provider"
        : "",
      service_line_requested:
        state === "NSW"
          ? "Path valve requested - with distributor"
          : "Gas line requested - with distributor",
      meter_requested:
        state === "NSW"
          ? "Meter kit requested - with distributor"
          : "Meter requested - with distributor",
    };

    return (
      lodgedStatuses[rawRequestStatus as keyof typeof lodgedStatuses] || ""
    );
  }

  // Handling FINISHED status
  if (
    requestStatus === RequestStatusEnum.FINISHED ||
    requestStatus === RequestStatusEnum.PROCESSED ||
    requestStatus === RequestStatusEnum.CT_NOT_REQUIRED
  ) {
    return (
      finishConnectedStatuses[
        rawRequestStatus as keyof typeof finishConnectedStatuses
      ] || ""
    );
  }

  // Handling CONNECTED status
  if (
    requestStatus === RequestStatusEnum.CONNECTED &&
    rawRequestStatus === "connected"
  ) {
    return connectedStatus;
  }

  // Handling COMPLETED status
  if (
    requestStatus === RequestStatusEnum.COMPLETED &&
    rawRequestStatus === "complete"
  ) {
    return completedStatus;
  }

  return "";
};

export const getStatusIconString = (
  requestStatus: RequestStatusEnum,
  messagePause: boolean,
  requestType: RequestTypeEnum
): string => {
  if (
    messagePause &&
    (requestType === RequestTypeEnum.NEW_CONNECTION ||
      requestType === RequestTypeEnum.HANDOVER)
  ) {
    return "pause";
  }
  const statusMessages = {
    [RequestStatusEnum.COMPLETED]: "connector",
    [RequestStatusEnum.DRAFT]: "pencil",
    [RequestStatusEnum.DRAFT_ASSIGNED]: "assigned",
    [RequestStatusEnum.CONNECTED]: "clock",
    [RequestStatusEnum.LODGED]: "clock",
    [RequestStatusEnum.CT_SUBMITTED]: "clock",
    [RequestStatusEnum.IN_PROGRESS]: "clock",
    [RequestStatusEnum.FAILED]: "clock",
    [RequestStatusEnum.FINISHED]: "tick",
    [RequestStatusEnum.CT_NOT_REQUIRED]: "tick",
    [RequestStatusEnum.PROCESSED]: "tick",
    [RequestStatusEnum.CANCELLED]: "cancel",
    [RequestStatusEnum.DISCARDED]: "none",
    [RequestStatusEnum.NONE]: "none",
    [RequestStatusEnum.AWAITING_ACCOUNT_HOLDER]: "pencil",
    [RequestStatusEnum.REVIEW]: "pencil",
    [RequestStatusEnum.PROCESSING]: "clock",
    [RequestStatusEnum.SCHEDULED]: "clock",
    [RequestStatusEnum.EIC_SENT]: "clock",
  };
  return statusMessages[requestStatus as keyof typeof statusMessages] || "none";
};

export const getSmallCase = (str: string) => str.toLowerCase();
/* eslint-enable */

export const isAllowedStateForElec = (state: string) => {
  const allowedStates = ["ACT", "NSW", "SA", "VIC"];
  return allowedStates.includes(state);
};

export const isAllowedForQldElec = (postcode: string) => {
  const allowedPostcodes = [
    "4390",
    "4385",
    "4384",
    "4387",
    "4383",
    "4380",
    "4496",
    "4494",
  ];

  return allowedPostcodes.includes(postcode);
};

interface IAddressObject {
  lot_number: string;
  street_number: string;
  // streetNumberSuffix
  street_name: string;
  street_suffix: string;
  street_type: string;
  postcode: string;
  dp_number: string;
  // blockNumber: values["premise-dpNumber"],
  // sectionNumber: values["premise-dpNumber"],
  suburb: string;
  state: string;
  building_name: string;
}

export const fromAddressObjectToString = (address: IAddressObject) => {
  return `${address.street_number} ${address.street_name} ${address.street_suffix} ${address.street_type}, ${address.suburb}, ${address.state} ${address.postcode} `;
};

export const updateToDiscard = async (
  requestId: string,
  reFetchPremises: () => void
) => {
  try {
    await apiClient.put(`/draft/discard/${requestId}`);
    reFetchPremises();
  } catch (error) {
    console.log(error);
  }
};

export const formatAddressFromBulkSelection = (premise: any): string => {
  if (!premise?.site_details) {
    return "";
  }

  const { street_name, street_type, street_suffix, suburb, state, postcode } =
    premise.site_details;
  const streetDetails = `${street_name} ${getStreetTypeFromKrakenValue(
    street_type
  )}${
    street_suffix ? ` ${street_suffix}` : ""
  }, ${suburb}, ${state} ${postcode}`;
  return streetDetails;
};

export const getFieldsDataFromAddress = (
  addressObj: PremiseDetails,
  key: string
): string => {
  switch (key) {
    case "floor_number":
    case "unit_number":
    case "unit_type":
      return addressObj[key];
    default:
      if (key === "dp_number")
        return (
          addressObj.site_details[key as keyof SiteDetails] || ""
        ).replace("DP", "");
      else return addressObj.site_details[key as keyof SiteDetails] || "";
  }
};
